import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Download } from "lucide-react";
import { SITE, IMAGES } from "../data/site";
import { PRODUCTS, CATEGORIES } from "../data/products";
import { MaskedLine, Reveal, Eyebrow } from "../components/Reveal";
import { Marquee } from "../components/Marquee";

export default function Products() {
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 500], [0, 140]);

  return (
    <>
      <section data-testid="products-hero" className="relative isolate flex min-h-[70vh] items-end overflow-hidden noise-surface">
        <motion.div style={{ y: yBg }} className="absolute inset-0 -z-10">
          <img src={IMAGES.towers} alt="Refinery plant towers" className="h-[115%] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A101D] via-[#0A101D]/70 to-[#0A101D]/40" />
        </motion.div>
        <div className="mx-auto w-full max-w-7xl px-5 pb-16 pt-40 lg:px-8">
          <Eyebrow>Product range</Eyebrow>
          <h1 className="mt-5 font-display text-5xl font-bold uppercase leading-[0.98] text-white sm:text-7xl">
            <MaskedLine delay={0.15}>Global products.</MaskedLine>
            <MaskedLine delay={0.3} innerClassName="text-amber-500">Reliable supply.</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-300"
          >
            From petroleum products and rubber raw materials to waxes, lubricants and specialty chemicals, Terra
            Bloom supports customers world over with reliable raw materials supply all year. Every product is backed
            by our commitment to consistent quality, competitive sourcing, dependable logistics and timely delivery.
          </motion.p>
        </div>
      </section>

      <Marquee items={["FOB Jebel Ali", "CFR / CIF Worldwide", "SGS Inspected", "Flexi-tank — ISO Tank — Drum — Bale — Bulk"]} />

      <section data-testid="products-list" className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          {CATEGORIES.map((cat, ci) => {
            const items = PRODUCTS.filter((p) => p.category === cat.id);
            return (
              <div key={cat.id} className={ci > 0 ? "mt-20" : ""} data-testid={`category-${cat.id}`}>
                <Reveal>
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 pb-4">
                    <span className="font-mono text-xs text-amber-600">0{ci + 1}</span>
                    <h2 className="font-display text-3xl font-bold uppercase tracking-wide text-[#0E1526] sm:text-4xl">
                      {cat.label}
                    </h2>
                    <p className="text-sm text-slate-500">{cat.blurb}</p>
                  </div>
                </Reveal>
                <div className="space-y-0">
                  {items.map((p) => {
                    const i = PRODUCTS.indexOf(p);
                    return (
                      <Reveal key={p.slug} delay={0.05}>
                        <Link
                          to={`/products/${p.slug}`}
                          data-testid={`products-list-${p.slug}`}
                          className={`group grid items-center gap-8 border-t border-black/10 py-12 transition-colors hover:bg-[#F8FAFC] md:grid-cols-[80px_1fr_240px] md:px-6 ${
                            i === PRODUCTS.length - 1 ? "border-b" : ""
                          }`}
                        >
                          <span className="font-mono text-sm text-amber-600">0{i + 1}</span>
                          <div>
                            <h3 className="font-display text-3xl font-semibold uppercase tracking-wide text-[#0E1526] transition-colors group-hover:text-amber-600 sm:text-4xl">
                              {p.shortName}
                            </h3>
                            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">{p.tagline}</p>
                            <div className="mt-4 flex flex-wrap gap-2">
                              {p.applications.slice(0, 3).map((a) => (
                                <span key={a} className="border border-black/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                  {a}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 md:justify-end">
                            <div className="clipped-frame hidden h-24 w-40 overflow-hidden md:block">
                              <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                            </div>
                            <ArrowUpRight className="h-6 w-6 text-slate-400 transition-all group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-amber-600" />
                          </div>
                        </Link>
                      </Reveal>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <div className="mt-20" data-testid="category-steel">
            <Reveal>
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 pb-4">
                <span className="font-mono text-xs text-amber-600">03</span>
                <h2 className="font-display text-3xl font-bold uppercase tracking-wide text-[#0E1526] sm:text-4xl">
                  Steel Products
                </h2>
                <p className="text-sm text-slate-500">Line pipe, structural steels, TMT bars and piling pipes.</p>
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <Link
                to="/steel"
                data-testid="products-list-steel"
                className="group grid items-center gap-8 border-y border-black/10 py-12 transition-colors hover:bg-[#F8FAFC] md:grid-cols-[80px_1fr_240px] md:px-6"
              >
                <span className="font-mono text-sm text-amber-600">10</span>
                <div>
                  <h3 className="font-display text-3xl font-semibold uppercase tracking-wide text-[#0E1526] transition-colors group-hover:text-amber-600 sm:text-4xl">
                    Steel Products
                  </h3>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">
                    Mill-direct steel for energy, water and construction — API 5L line pipe, EN/ASTM/IS structural
                    steels, TMT bars and seamless/ERW piling pipes, with mill test certificates on every lot.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {["API 5L line pipe", "Structural steels", "TMT bars"].map((a) => (
                      <span key={a} className="border border-black/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                        {a}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-3 md:justify-end">
                  <div className="clipped-frame hidden h-24 w-40 overflow-hidden md:block">
                    <img src={IMAGES.steelPlant} alt="Steel products" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  </div>
                  <ArrowUpRight className="h-6 w-6 text-slate-400 transition-all group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-amber-600" />
                </div>
              </Link>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="mt-16 flex flex-wrap items-center justify-between gap-6 border border-amber-500/40 bg-[#F5F5F2] p-8">
            <div>
              <h3 className="font-display text-2xl font-semibold uppercase text-[#0E1526]">Full specs in one document</h3>
              <p className="mt-2 text-sm text-slate-600">Download the Terra Bloom product brochure with all grade tables and packaging options.</p>
            </div>
            <a
              href={SITE.brochure}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="products-brochure-button"
              onClick={() => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/brochure-download`, { method: "POST" }).catch(() => {})}
              className="inline-flex items-center gap-2 bg-amber-500 px-7 py-3.5 text-sm font-semibold text-[#0A101D] transition-colors hover:bg-amber-400"
            >
              <Download className="h-4 w-4" /> Download Brochure (PDF)
            </a>
          </Reveal>
        </div>
      </section>
    </>
  );
}
